import { Menu as BaseMenu } from '@base-ui/react/menu'
import { Check, type LucideIcon } from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'
import { cn } from '../lib/cn'
import type { Hotkey } from '../lib/hotkeys'
import { LayerProvider, useLayer, useLink, Z } from './context'
import { Kbd } from './Kbd'
import { overlayClass } from './Popover'
import type { ActionDef } from './types'

export type MenuEntry =
  | {
      type?: 'item'
      id: string
      label: string
      description?: string
      icon?: LucideIcon
      hotkey?: Hotkey
      onSelect?: () => void | Promise<void>
      href?: string
      disabled?: boolean
      /** steht an der deaktivierten Zeile */
      disabledReason?: string
      tone?: 'danger'
      /** rechts: Zähler, Zustand */
      meta?: ReactNode
    }
  | {
      type: 'checkbox'
      id: string
      label: string
      checked: boolean
      onCheckedChange: (checked: boolean) => void
      disabled?: boolean
    }
  | {
      type: 'radio'
      id: string
      label: string
      checked: boolean
      onSelect: () => void
      meta?: ReactNode
      disabled?: boolean
    }
  | { type: 'label'; id: string; label: string }
  | { type: 'separator'; id: string }

export interface DropdownMenuProps {
  /** Auslöser (Button, IconButton …) */
  trigger: ReactElement<Record<string, unknown>>
  items: MenuEntry[]
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  /** Mindestbreite */
  width?: 'auto' | 'sm' | 'md' | 'trigger'
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** bleibt nach Auswahl von Kontrollkästchen offen (Spalten-Menü) */
  footer?: ReactNode
}

/** Aus Aktionen (PageHeader, Zeilenmenü) werden Menüeinträge. */
export const actionsToMenu = (actions: readonly ActionDef[]): MenuEntry[] =>
  actions.map((action) => ({
    id: action.id,
    label: action.label,
    icon: action.icon,
    hotkey: action.hotkey,
    onSelect: action.onSelect,
    disabled: action.disabled,
    disabledReason: action.disabledReason,
    tone: action.variant === 'danger' ? 'danger' : undefined,
  }))

const itemClass =
  'flex min-h-8 cursor-default items-center gap-2 rounded-sm px-2 py-1 text-dense outline-none select-none touch:min-h-11 touch:text-touch data-highlighted:bg-overlay-hover data-disabled:text-fg-disabled'

const WIDTH = { auto: 'min-w-44', sm: 'min-w-56', md: 'min-w-72', trigger: 'min-w-(--anchor-width)' } as const

/** Menü an einer Schaltfläche: Aktionen der Zeile, „···“, Ansichten, Spalten. */
export function DropdownMenu({
  trigger,
  items,
  side = 'bottom',
  align = 'start',
  width = 'auto',
  open,
  onOpenChange,
  footer,
}: DropdownMenuProps) {
  const zIndex = useLayer(Z.popover)
  const Link = useLink()
  return (
    <BaseMenu.Root open={open} onOpenChange={(next) => onOpenChange?.(next)}>
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner side={side} align={align} sideOffset={4} collisionPadding={8} style={{ zIndex }}>
          <BaseMenu.Popup
            className={cn(overlayClass, WIDTH[width], 'max-h-(--available-height) max-w-88 overflow-y-auto p-1')}
          >
            <LayerProvider z={zIndex}>
              {items.map((entry) => {
                if (entry.type === 'separator')
                  return <BaseMenu.Separator key={entry.id} className="-mx-1 my-1 h-px bg-line" />
                if (entry.type === 'label') {
                  return (
                    <div
                      key={entry.id}
                      className="px-2 pt-2 pb-1 text-micro text-fg-subtle uppercase"
                      role="presentation"
                    >
                      {entry.label}
                    </div>
                  )
                }
                if (entry.type === 'checkbox') {
                  return (
                    <BaseMenu.CheckboxItem
                      key={entry.id}
                      checked={entry.checked}
                      onCheckedChange={entry.onCheckedChange}
                      disabled={entry.disabled}
                      closeOnClick={false}
                      className={itemClass}
                    >
                      <span
                        className={cn(
                          'flex size-4 shrink-0 items-center justify-center rounded-xs border',
                          entry.checked ? 'border-accent bg-accent text-on-accent' : 'border-line-control bg-surface',
                        )}
                        aria-hidden
                      >
                        {entry.checked && <Check className="size-3" strokeWidth={2.5} />}
                      </span>
                      <span className="truncate-line">{entry.label}</span>
                    </BaseMenu.CheckboxItem>
                  )
                }
                if (entry.type === 'radio') {
                  return (
                    <BaseMenu.Item
                      key={entry.id}
                      disabled={entry.disabled}
                      onClick={entry.onSelect}
                      className={itemClass}
                    >
                      <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
                        {entry.checked && <Check className="size-4" strokeWidth={2} />}
                      </span>
                      <span className={cn('truncate-line flex-1', entry.checked && 'font-medium')}>{entry.label}</span>
                      {entry.checked && <span className="sr-only">(aktiv)</span>}
                      {entry.meta !== undefined && (
                        <span className="num text-caption text-fg-subtle">{entry.meta}</span>
                      )}
                    </BaseMenu.Item>
                  )
                }
                const Icon = entry.icon
                const body = (
                  <>
                    {Icon && (
                      <Icon className="size-4 shrink-0 text-fg-muted touch:size-5" strokeWidth={1.75} aria-hidden />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate-line">{entry.label}</span>
                      {(entry.description || (entry.disabled && entry.disabledReason)) && (
                        <span className="block text-caption text-fg-subtle">
                          {entry.disabled && entry.disabledReason ? entry.disabledReason : entry.description}
                        </span>
                      )}
                    </span>
                    {entry.meta !== undefined && <span className="num text-caption text-fg-subtle">{entry.meta}</span>}
                    {entry.hotkey && <Kbd hotkey={entry.hotkey} variant="plain" className="hidden md:inline-flex touch:hidden" />}
                  </>
                )
                const classes = cn(itemClass, entry.tone === 'danger' && !entry.disabled && 'text-danger-fg')
                if (entry.href && !entry.disabled) {
                  return (
                    <BaseMenu.LinkItem key={entry.id} className={classes} render={<Link href={entry.href} />}>
                      {body}
                    </BaseMenu.LinkItem>
                  )
                }
                return (
                  <BaseMenu.Item
                    key={entry.id}
                    disabled={entry.disabled}
                    onClick={() => void entry.onSelect?.()}
                    className={classes}
                  >
                    {body}
                  </BaseMenu.Item>
                )
              })}
              {footer}
            </LayerProvider>
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  )
}
